import { useId, useState, type ReactNode } from 'react'

import { cn } from '@/lib/cn'
import { formatNumber, parseDecimal } from '@/lib/format'

import { Field } from './Field'
import { Icon } from './Icon'

export type NumberStepperProps = {
  value: number | null
  onChange: (value: number | null) => void
  step?: number
  min?: number
  max?: number
  /** Знаков после запятой. */
  digits?: number
  unit?: ReactNode
  label?: ReactNode
  hint?: ReactNode
  error?: ReactNode
  /** lg — для тренировки: крупные кнопки и цифры. */
  size?: 'md' | 'lg'
  disabled?: boolean
  id?: string
  className?: string
}

function clamp(value: number, min?: number, max?: number): number {
  let v = value
  if (min !== undefined) v = Math.max(min, v)
  if (max !== undefined) v = Math.min(max, v)
  return v
}

function round(value: number, digits: number): number {
  const f = 10 ** digits
  return Math.round(value * f) / f
}

const asText = (value: number | null, digits: number) =>
  value === null ? '' : formatNumber(value, { digits }).replace(/\s/g, '')

/** Поле числа с кнопками − и +; принимает запятую. */
export function NumberStepper({
  value,
  onChange,
  step = 1,
  min,
  max,
  digits = 0,
  unit,
  label,
  hint,
  error,
  size = 'md',
  disabled,
  id,
  className,
}: NumberStepperProps) {
  const autoId = useId()
  const inputId = id ?? autoId
  const [text, setText] = useState(() => asText(value, digits))

  // Значение поменяли снаружи — показать его, если в поле не то же самое число.
  const [shown, setShown] = useState(value)
  if (shown !== value) {
    setShown(value)
    if (parseDecimal(text) !== value) setText(asText(value, digits))
  }

  const commit = (next: number | null) => {
    const normalized = next === null ? null : round(clamp(next, min, max), digits)
    setText(asText(normalized, digits))
    onChange(normalized)
  }

  const bump = (direction: 1 | -1) => commit((value ?? min ?? 0) + direction * step)

  const big = size === 'lg'
  const button = cn(
    'flex shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-fg',
    'hover:bg-surface-2 active:bg-surface-2 disabled:opacity-40',
    big ? 'h-14 w-14' : 'h-11 w-11 lg:h-10 lg:w-10',
  )

  return (
    <Field id={inputId} label={label} hint={hint} error={error} className={className}>
      <div className="flex items-stretch gap-2">
        <button
          type="button"
          className={button}
          onClick={() => bump(-1)}
          disabled={disabled || (min !== undefined && value !== null && value <= min)}
          aria-label="Меньше"
        >
          <Icon name="minus" size={big ? 24 : 18} />
        </button>
        <div className="relative min-w-0 flex-1">
          <input
            id={inputId}
            inputMode={digits > 0 ? 'decimal' : 'numeric'}
            autoComplete="off"
            disabled={disabled}
            value={text}
            aria-invalid={error ? true : undefined}
            onChange={(e) => setText(e.target.value)}
            onBlur={() => commit(parseDecimal(text))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit(parseDecimal(text))
              if (e.key === 'ArrowUp') {
                e.preventDefault()
                bump(1)
              }
              if (e.key === 'ArrowDown') {
                e.preventDefault()
                bump(-1)
              }
            }}
            className={cn(
              'w-full min-w-0 rounded-lg border border-border bg-surface text-center font-medium text-fg tabular-nums',
              'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/25',
              'aria-[invalid=true]:border-danger',
              big ? 'h-14 text-2xl' : 'h-11 text-base lg:h-10',
              unit ? 'pr-9' : null,
            )}
          />
          {unit ? (
            <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted">
              {unit}
            </span>
          ) : null}
        </div>
        <button
          type="button"
          className={button}
          onClick={() => bump(1)}
          disabled={disabled || (max !== undefined && value !== null && value >= max)}
          aria-label="Больше"
        >
          <Icon name="plus" size={big ? 24 : 18} />
        </button>
      </div>
    </Field>
  )
}
